import { useState, useCallback } from 'react';
import { Observer, Disposable, Subscription } from 'relay-runtime';
import { RelayPaginationProp, RefetchOptions } from 'react-relay';

export type WithNullAndUndefined<T> = T | null | undefined;

type ObserverOrCallback = WithNullAndUndefined<
  Observer<void> | ((error: WithNullAndUndefined<Error>) => void)
>;

const updateObserverOrCallback = (
  observerOrCallback: ObserverOrCallback,
  stopLoading: () => void
): ObserverOrCallback => {
  if (observerOrCallback && typeof observerOrCallback !== 'function') {
    return {
      ...observerOrCallback,
      complete: () => {
        stopLoading();
        observerOrCallback.complete?.();
      },
      error: (error: Error) => {
        stopLoading();
        observerOrCallback.error?.(error);
      },
      unsubscribe: (subscription: Subscription) => {
        stopLoading();
        observerOrCallback.unsubscribe?.(subscription);
      },
    };
  }
  return (error: WithNullAndUndefined<Error>) => {
    stopLoading();
    observerOrCallback?.(error);
  };
};

function useLoadMore(
  relayLoadMore: RelayPaginationProp['loadMore']
): {
  isLoading: boolean;
  loadMore: RelayPaginationProp['loadMore'];
} {
  const [isLoading, setIsLoading] = useState(false);
  const loadMore = useCallback(
    (
      pageSize: number,
      observerOrCallback?: ObserverOrCallback,
      options?: RefetchOptions | null
    ): WithNullAndUndefined<Disposable> => {
      setIsLoading(true);
      return relayLoadMore(
        pageSize,
        updateObserverOrCallback(observerOrCallback, () => setIsLoading(false)),
        options
      );
    },
    [relayLoadMore]
  );
  return {
    isLoading,
    loadMore,
  };
}
export default useLoadMore;
